import React, { useEffect, useState } from 'react';
import { Download, ChevronRight, Share2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Minimal install entry point for the Profile menu list.
 * Renders only when the app is NOT installed. Uses the same beforeinstallprompt
 * event as the feed banner. On iOS (which doesn't expose the event), shows a
 * short toast with Add-to-Home-Screen instructions.
 */
export default function InstallLink() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsInstalled(standalone);
    if (standalone) return;

    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()) && !window.MSStream;
    setIsIOS(ios);

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setIsInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (isInstalled) return null;

  const handleClick = async () => {
    // Also un-hide the banner so it reappears on the feed next time.
    localStorage.removeItem('frootex_install_dismissed');
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return;
    }
    if (isIOS) {
      toast('Tap Share ⎋ → then "Add to Home Screen"', { icon: <Share2 className="w-4 h-4" /> });
      return;
    }
    toast('Open this site in Chrome — the install prompt appears automatically', {
      icon: <Download className="w-4 h-4" />,
    });
  };

  return (
    <button
      data-testid="install-app-menu-link"
      onClick={handleClick}
      className="w-full flex items-center gap-3 p-4 hover:bg-[#EFF2F7] transition-colors border-b border-[#E4E8EF]"
    >
      <div className="w-9 h-9 rounded-xl bg-[#0B4F3F]/10 flex items-center justify-center">
        <Download className="w-4 h-4 text-[#0B4F3F]" />
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-medium text-[#0B1620]">Install Frootex app</p>
        <p className="text-xs text-[#5B6675]">Faster access · works offline</p>
      </div>
      <ChevronRight className="w-4 h-4 text-[#5B6675]" />
    </button>
  );
}
